/**
 * The control for one intake question. Single-choice answers call `onAnswer` as soon as they are
 * picked (INT-002). Lists and measurements keep a Continue button.
 */
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import type { IntakeAnswers, QuestionId } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { focusFirstError } from "@lattice/ui/focusFirstError";
import { Field } from "@lattice/ui/Field";
import { CheckIcon, PlusIcon } from "@lattice/ui/icons";
import { Input } from "@lattice/ui/Input";
import type { RadioOption } from "@lattice/ui/RadioGroup";
import { RadioGroup } from "@lattice/ui/RadioGroup";
import { SegmentedControl } from "@lattice/ui/SegmentedControl";
import { ALLERGIES, CONDITIONS, DIET, EXERCISE_TYPE, GOAL, MEDICATIONS, SEX, SUPPLEMENTS } from "./questions.ts";
/*
  A single-choice question. It moves on as soon as it is answered (INT-002), so the arrow keys only
  look through the options and Space, Enter or a press answers.
*/
function OneOf<Value extends string>({
  labelledBy,
  options,
  value,
  onPick
}: {
  readonly labelledBy: string;
  readonly options: readonly RadioOption<Value>[];
  readonly value: Value | null;
  readonly onPick: (value: Value) => void;
}) {
  return <RadioGroup labelledBy={labelledBy} variant="cards" manual options={options} value={value} onChange={onPick} />;
}
function DateField({
  value,
  onAnswer
}: {
  readonly value: string;
  readonly onAnswer: (answer: NonNullable<IntakeAnswers["date_of_birth"]>) => void;
}) {
  const [date, setDate] = useState(value);
  const [missing, setMissing] = useState(false);
  return <form noValidate className="x78zum5 xdt5ytf x626il7 xh8yej3" onSubmit={event => {
    event.preventDefault();
    setMissing(date === "");
    if (date !== "") onAnswer({
      date
    });else focusFirstError(event.currentTarget);
  }}>
      <Field label="Date of birth" error={missing ? "Enter your date of birth." : null}>
        {control => <Input {...control} type="date" size="lg" autoComplete="bday" value={date} onChange={setDate} />}
      </Field>
      <Button type="submit" fullWidth>
        Continue
      </Button>
    </form>;
}

/** Which of the two measurements is outside a typical adult range. */
interface OutOfRange {
  readonly height: boolean;
  readonly weight: boolean;
}
function MeasureField({
  value,
  onAnswer
}: {
  readonly value: IntakeAnswers["height_weight"];
  readonly onAnswer: (answer: NonNullable<IntakeAnswers["height_weight"]>) => void;
}) {
  const [metric, setMetric] = useState(false);
  const [heightCm, setHeightCm] = useState(value ? String(value.heightCm) : "");
  const [weightKg, setWeightKg] = useState(value ? String(value.weightKg) : "");
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");
  const [pounds, setPounds] = useState("");
  const [problem, setProblem] = useState<OutOfRange>({
    height: false,
    weight: false
  });
  const heightError = !problem.height ? null : metric ? "Enter a height between 100 and 250 cm." : "Enter a height between 3 feet 4 inches and 8 feet 2 inches.";
  const weightError = !problem.weight ? null : metric ? "Enter a weight between 30 and 300 kg." : "Enter a weight between 66 and 660 lb.";
  const submit = () => {
    const next = metric ? {
      heightCm: Number(heightCm),
      weightKg: Number(weightKg)
    } : {
      heightCm: Math.round((Number(feet) * 12 + Number(inches)) * 2.54),
      weightKg: Math.round(Number(pounds) / 2.20462262)
    };
    const found = {
      height: !Number.isFinite(next.heightCm) || next.heightCm < 100 || next.heightCm > 250,
      weight: !Number.isFinite(next.weightKg) || next.weightKg < 30 || next.weightKg > 300
    };
    setProblem(found);
    if (!found.height && !found.weight) onAnswer(next);
    return !found.height && !found.weight;
  };
  return <form noValidate className="x78zum5 xdt5ytf x626il7 xh8yej3" onSubmit={event => {
    event.preventDefault();
    if (!submit()) focusFirstError(event.currentTarget);
  }}>
      <Button variant="secondary" size="md" onClick={() => setMetric(on => !on)}>
        {metric ? "Use feet and pounds" : "Use cm and kg"}
      </Button>
      {metric ? <div className="xrvj5dj x1o61qjw x626il7">
          <Field label="Height, cm" error={heightError}>
            {control => <Input {...control} type="number" inputMode="numeric" size="lg" min={100} max={250} value={heightCm} onChange={setHeightCm} />}
          </Field>
          <Field label="Weight, kg" error={weightError}>
            {control => <Input {...control} type="number" inputMode="numeric" size="lg" min={30} max={300} value={weightKg} onChange={setWeightKg} />}
          </Field>
        </div> : <>
          <div className="xrvj5dj x1o61qjw x626il7">
            <Field label="Feet" error={heightError}>
              {control => <Input {...control} type="number" inputMode="numeric" size="lg" min={3} max={8} value={feet} onChange={setFeet} />}
            </Field>
            <Field label="Inches">
              {control => <Input {...control} type="number" inputMode="numeric" size="lg" min={0} max={11} value={inches} onChange={setInches} />}
            </Field>
          </div>
          <Field label="Weight, lb" error={weightError}>
            {control => <Input {...control} type="number" inputMode="numeric" size="lg" min={66} max={660} value={pounds} onChange={setPounds} />}
          </Field>
        </>}
      <Button type="submit" fullWidth>
        Continue
      </Button>
    </form>;
}
function ListField({
  suggestions,
  value,
  onAnswer
}: {
  readonly suggestions: readonly string[];
  readonly value: readonly string[];
  readonly onAnswer: (answer: readonly string[]) => void;
}) {
  const [picked, setPicked] = useState<readonly string[]>(value);
  const [typed, setTyped] = useState("");
  const add = (item: string) => {
    if (picked.includes(item)) return;
    setPicked([...picked, item]);
    setTyped("");
  };

  /* What was typed counts on Continue even without Enter, so it is never dropped. */
  const entry = typed.trim();
  const answer = entry === "" || picked.includes(entry) ? picked : [...picked, entry];
  const chips = [...suggestions, ...picked.filter(item => !suggestions.includes(item))];
  return <div className="x78zum5 xdt5ytf x626il7 xh8yej3">
      <div className="x78zum5 x1a02dak x1173whf">
        {chips.map(item => <Button key={item} size="md" variant="secondary" pressed={picked.includes(item)}
      /* Plus to add, a tick once added, in the same slot so the row does not reflow. */ leadingIcon={picked.includes(item) ? <CheckIcon /> : <PlusIcon />} onClick={() => picked.includes(item) ? setPicked(picked.filter(entry => entry !== item)) : add(item)}>
            {item}
          </Button>)}
      </div>
      <Field label="Something else" hint="Type it and press Enter to add it." optional>
        {control => <Input {...control} size="lg" enterKeyHint="done" value={typed} onChange={setTyped} onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          if (typed.trim()) add(typed.trim());
        }
      }} />}
      </Field>
      {answer.length === 0 ? <Button variant="secondary" fullWidth onClick={() => onAnswer([])}>
          None of these
        </Button> : <Button fullWidth onClick={() => onAnswer(answer)}>
          Continue
        </Button>}
    </div>;
}
type ExerciseType = NonNullable<IntakeAnswers["exercise"]>["type"];
const DAYS = ["0", "1", "2", "3", "4", "5", "6", "7"] as const;
type Days = (typeof DAYS)[number];
const DAY_OPTIONS = DAYS.map(day => ({
  value: day,
  label: day
}));
function ExerciseField({
  value,
  onAnswer
}: {
  readonly value: IntakeAnswers["exercise"];
  readonly onAnswer: (answer: NonNullable<IntakeAnswers["exercise"]>) => void;
}) {
  const [days, setDays] = useState<number | null>(value?.daysPerWeek ?? null);
  const [kind, setKind] = useState<ExerciseType | null>(value?.type ?? null);
  const pickDays = (next: Days) => {
    const count = Number(next);
    setDays(count);
    if (count === 0) onAnswer({
      daysPerWeek: 0,
      type: "none"
    });
  };
  const pickKind = (next: ExerciseType) => {
    setKind(next);
    if (days !== null && days > 0) onAnswer({
      daysPerWeek: days,
      type: next
    });
  };
  return <div className="x78zum5 xdt5ytf x626il7 xh8yej3">
      <p id="exercise-days" className="x1ghz6dp xl4dl4e x1jty25b">
        Days each week
      </p>
      <SegmentedControl labelledBy="exercise-days" fullWidth manual options={DAY_OPTIONS} value={DAYS.find(day => Number(day) === days) ?? null} onChange={pickDays} />
      {days !== null && days > 0 && <>
          <p id="exercise-kind" className="x1ghz6dp xl4dl4e x1jty25b">
            Main kind of work
          </p>
          <OneOf labelledBy="exercise-kind" options={EXERCISE_TYPE} value={kind} onPick={pickKind} />
        </>}
    </div>;
}
const YES_NO = [{
  value: "yes",
  label: "Yes"
}, {
  value: "no",
  label: "No"
}] as const;

/**
 * The control for one question. `labelledBy` is the id of the question's own words on the screen
 * around it, which name each choice group.
 *
 * The four list questions are keyed by question, so each starts from its own saved answer. Without
 * the key React keeps one list across them, and picks on one question carry into the next.
 */
export function QuestionBody({
  id,
  answers,
  labelledBy,
  onAnswer
}: {
  readonly id: QuestionId;
  readonly answers: IntakeAnswers;
  readonly labelledBy: string;
  readonly onAnswer: (id: QuestionId, answer: NonNullable<IntakeAnswers[QuestionId]>) => void;
}) {
  if (id === "date_of_birth") return <DateField value={answers.date_of_birth?.date ?? ""} onAnswer={answer => onAnswer(id, answer)} />;
  if (id === "sex") return <OneOf labelledBy={labelledBy} options={SEX} value={answers.sex ?? null} onPick={answer => onAnswer(id, answer)} />;
  if (id === "pregnancy") return <OneOf labelledBy={labelledBy} options={YES_NO} value={answers.pregnancy === undefined ? null : answers.pregnancy ? "yes" : "no"} onPick={answer => onAnswer(id, answer === "yes")} />;
  if (id === "height_weight") return <MeasureField value={answers.height_weight} onAnswer={answer => onAnswer(id, answer)} />;
  if (id === "conditions") return <ListField key={id} suggestions={CONDITIONS} value={answers.conditions ?? []} onAnswer={answer => onAnswer(id, [...answer])} />;
  if (id === "medications") return <ListField key={id} suggestions={MEDICATIONS} value={answers.medications ?? []} onAnswer={answer => onAnswer(id, [...answer])} />;
  if (id === "allergies") return <ListField key={id} suggestions={ALLERGIES} value={answers.allergies ?? []} onAnswer={answer => onAnswer(id, [...answer])} />;
  if (id === "supplements") return <ListField key={id} suggestions={SUPPLEMENTS} value={(answers.supplements ?? []).map(item => item.name)} onAnswer={names => onAnswer(id, names.map(name => ({
    name,
    ingredientId: null,
    dailyDose: null
  })))} />;
  if (id === "diet") return <OneOf labelledBy={labelledBy} options={DIET} value={answers.diet ?? null} onPick={answer => onAnswer(id, answer)} />;
  if (id === "exercise") return <ExerciseField value={answers.exercise} onAnswer={answer => onAnswer(id, answer)} />;
  return <OneOf labelledBy={labelledBy} options={GOAL} value={answers.goal ?? null} onPick={answer => onAnswer(id, answer)} />;
}